import { Marquee as ArkMarquee } from '@ark-ui/solid/marquee';
import { createUniqueId, For } from 'solid-js';
import { cn } from 'tailwind-variants';
import type { UseMarqueeContext } from '@ark-ui/solid/marquee';
import type { Accessor, JSX } from 'solid-js';

type Props<T> = {
	autoFill?: boolean;
	children: (item: T, index: Accessor<number>) => JSX.Element;
	delay?: number;
	description: string;
	itemClass?: string | ((item: T, index: Accessor<number>) => string | undefined);
	items: ReadonlyArray<T> | undefined;
	listClass?: string;
	outerClass?: string;
	pauseOnInteraction?: boolean;
	spacing?: string;
	speed?: number;
};

export function Marquee<T>(props: Props<T>) {
	const id = createUniqueId();
	const items = () => props.items ?? EMPTY_ITEMS;

	return (
		<ArkMarquee.Root
			autoFill={props.autoFill ?? true}
			delay={props.delay ?? 1}
			pauseOnInteraction={props.pauseOnInteraction ?? true}
			speed={props.speed ?? 30}
			spacing={props.spacing ?? '0px'}
			class={cn('overflow-x-hidden', props.outerClass)}
			aria-label={props.description}
		>
			<ArkMarquee.Viewport>
				<ArkMarquee.Context>
					{(marqueeApi) => (
						<MarqueeContents
							id={id}
							marqueeApi={marqueeApi}
							items={items()}
							itemClass={props.itemClass}
							listClass={props.listClass}
						>
							{props.children}
						</MarqueeContents>
					)}
				</ArkMarquee.Context>
			</ArkMarquee.Viewport>
		</ArkMarquee.Root>
	);
}

function MarqueeContents<T>(
	props: Pick<Props<T>, 'children' | 'itemClass' | 'listClass'> & {
		id: string;
		items: ReadonlyArray<T>;
		marqueeApi: UseMarqueeContext;
	},
) {
	return (
		<For each={Array.from({ length: props.marqueeApi().contentCount }, (_, index) => index)}>
			{(contentIndex) => {
				const contentProps = () => props.marqueeApi().getContentProps({ index: contentIndex });

				return (
					<div {...contentProps()} class={cn(contentProps().class, 'animate-marquee-x', props.listClass)}>
						<For each={props.items}>
							{(item, index) => (
								<ArkMarquee.Item
									id={`${props.id}:item:${contentIndex}:${index()}`}
									class={cn('shrink-0 grow-0 object-contain', resolveItemClass(props.itemClass, item, index))}
								>
									{props.children(item, index)}
								</ArkMarquee.Item>
							)}
						</For>
					</div>
				);
			}}
		</For>
	);
}

const EMPTY_ITEMS: ReadonlyArray<never> = [];

function resolveItemClass<T>(
	itemClass: string | ((item: T, index: Accessor<number>) => string | undefined) | undefined,
	item: T,
	index: Accessor<number>,
) {
	return typeof itemClass === 'function' ? itemClass(item, index) : itemClass;
}
